// Chart primitives for the government dashboard. Thin marks, 4 px rounded data ends, hairline grid,
// hover tooltips, legend for multi-series and a table view for every chart.
// Colours come from CSS roles (--series-1/2, --grid, --axis, text tokens) defined in styles.css.

import { useMemo, useRef, useState, type ReactNode } from 'react';

const compact = (n: number) => (n >= 10000 ? `${Math.round(n / 1000)}K` : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K` : String(Math.round(n)));

/** Pointer position relative to the chart's <svg>, independent of which child received the event. */
function svgPoint(e: { clientX: number; clientY: number; currentTarget: Element }) {
  const r = (e.currentTarget.closest('svg') ?? e.currentTarget).getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

function useWidth(initial = 480) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(initial);
  const ro = useMemo(() => (typeof ResizeObserver === 'undefined' ? null : new ResizeObserver((e) => setW(Math.max(280, e[0].contentRect.width)))), []);
  const set = (el: HTMLDivElement | null) => {
    if (ref.current && ro) ro.unobserve(ref.current);
    (ref as { current: HTMLDivElement | null }).current = el;
    if (el && ro) ro.observe(el);
  };
  return [set, w] as const;
}

export function ChartFrame({ title, subtitle, legend, table, children }: { title: string; subtitle?: string; legend?: ReactNode; table: ReactNode; children: ReactNode }) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className="panel viz">
      <div className="viz-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <div className="muted small">{subtitle}</div>}
        </div>
        <button className="btn btn-small" onClick={() => setAsTable(!asTable)} aria-pressed={asTable}>{asTable ? 'Chart view' : 'Table view'}</button>
      </div>
      {legend && !asTable && <div className="legend">{legend}</div>}
      {asTable ? <div className="viz-table">{table}</div> : children}
    </section>
  );
}

export const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <span className="legend-item"><span className="legend-key" style={{ background: color }} />{label}</span>
);

// --- Horizontal bar chart (one series) ------------------------------------------------------

export interface BarDatum {
  key: string;
  label: string;
  value: number;
  detail?: string[];
}

export function BarChart({ data, unit }: { data: BarDatum[]; unit: string }) {
  const [setRef, width] = useWidth();
  const [hover, setHover] = useState<{ d: BarDatum; x: number; y: number } | null>(null);
  const labelW = 150;
  const valueW = 56;
  const rowH = 34;
  const barH = 18;
  const top = 8;
  const height = top + data.length * rowH + 22;
  const plotW = Math.max(60, width - labelW - valueW);
  const max = niceMax(Math.max(1, ...data.map((d) => d.value)));
  const x = (v: number) => (v / max) * plotW;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);

  return (
    <div className="chart" ref={setRef} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label={`Bar chart of ${unit} by district`}>
        <g transform={`translate(${labelW},${top})`}>
          {ticks.map((t) => (
            <g key={t} transform={`translate(${x(t)},0)`}>
              <line y1={0} y2={data.length * rowH} className="grid" />
              <text y={data.length * rowH + 16} className="axis-text" textAnchor="middle">{compact(t)}</text>
            </g>
          ))}
          <line x1={0} x2={0} y1={0} y2={data.length * rowH} className="baseline" />
          {data.map((d, i) => {
            const y = i * rowH + (rowH - barH) / 2;
            const w = x(d.value);
            const r = Math.min(4, w / 2, barH / 2);
            // Square at the baseline, 4 px rounded at the data end.
            const path = w <= 0 ? '' : `M0,${y} H${w - r} Q${w},${y} ${w},${y + r} V${y + barH - r} Q${w},${y + barH} ${w - r},${y + barH} H0 Z`;
            return (
              <g key={d.key}>
                <text x={-10} y={y + barH / 2} dy="0.35em" textAnchor="end" className="cat-text">{d.label}</text>
                {path && <path d={path} className={`bar ${hover?.d.key === d.key ? 'hover' : ''}`} />}
                <text x={w + 6} y={y + barH / 2} dy="0.35em" className="value-text">{d.value.toLocaleString()}</text>
                <rect x={-labelW} y={i * rowH} width={labelW + plotW + valueW} height={rowH} fill="transparent"
                  onMouseMove={(e) => setHover({ d, ...svgPoint(e) })} />
              </g>
            );
          })}
        </g>
      </svg>
      {hover && (
        <div className="tooltip" style={{ left: Math.min(hover.x + 12, width - 220), top: hover.y + 12 }}>
          <strong>{hover.d.label}</strong>
          <div>{hover.d.value.toLocaleString()} {unit}</div>
          {hover.d.detail?.map((t) => <div key={t} className="muted">{t}</div>)}
        </div>
      )}
    </div>
  );
}

// --- Line chart (≤ 4 series, one y axis) ----------------------------------------------------

export interface Series {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export function LineChart({ x, series }: { x: string[]; series: Series[] }) {
  const [setRef, width] = useWidth();
  const [hi, setHi] = useState<number | null>(null);
  const m = { top: 12, right: 112, bottom: 26, left: 40 };
  const height = 240;
  const pw = Math.max(80, width - m.left - m.right);
  const ph = height - m.top - m.bottom;
  const max = niceMax(Math.max(1, ...series.flatMap((s) => s.values)));
  const sx = (i: number) => (x.length <= 1 ? pw / 2 : (i / (x.length - 1)) * pw);
  const sy = (v: number) => ph - (v / max) * ph;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const every = Math.ceil(x.length / Math.max(2, Math.floor(pw / 70)));
  const fmtDay = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

  // End labels: nudge apart only if they collide (two series), keeping them attached to their line ends.
  const ends = series.map((s) => ({ s, y: sy(s.values[s.values.length - 1] ?? 0) }));
  if (ends.length === 2 && Math.abs(ends[0].y - ends[1].y) < 14) {
    const [a, b] = ends[0].y <= ends[1].y ? [ends[0], ends[1]] : [ends[1], ends[0]];
    const mid = (a.y + b.y) / 2;
    a.y = mid - 8;
    b.y = mid + 8;
  }

  return (
    <div className="chart" ref={setRef}>
      <svg width={width} height={height} role="img" aria-label="Daily trend line chart"
        onMouseMove={(e) => {
          const px = svgPoint(e).x - m.left;
          const i = Math.round((px / pw) * (x.length - 1));
          setHi(i >= 0 && i < x.length ? i : null);
        }}
        onMouseLeave={() => setHi(null)}>
        <g transform={`translate(${m.left},${m.top})`}>
          {ticks.map((t) => (
            <g key={t} transform={`translate(0,${sy(t)})`}>
              <line x1={0} x2={pw} className={t === 0 ? 'baseline' : 'grid'} />
              <text x={-8} dy="0.35em" textAnchor="end" className="axis-text">{compact(t)}</text>
            </g>
          ))}
          {x.map((d, i) => ((x.length - 1 - i) % every === 0 ? <text key={d} x={sx(i)} y={ph + 18} textAnchor="middle" className="axis-text">{fmtDay(d)}</text> : null))}
          {hi !== null && <line x1={sx(hi)} x2={sx(hi)} y1={0} y2={ph} className="crosshair" />}
          {series.map((s) => (
            <path key={s.key} d={s.values.map((v, i) => `${i ? 'L' : 'M'}${sx(i)},${sy(v)}`).join('')} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {series.map((s) => {
            const i = hi ?? s.values.length - 1;
            return <circle key={s.key} cx={sx(i)} cy={sy(s.values[i] ?? 0)} r={4} fill={s.color} className="dot-ring" />;
          })}
          {ends.map(({ s, y }) => (
            <text key={s.key} x={pw + 10} y={y} dy="0.35em" className="end-label">{s.label} {s.values[s.values.length - 1]?.toLocaleString()}</text>
          ))}
        </g>
      </svg>
      {hi !== null && (
        <div className="tooltip" style={{ left: Math.min(m.left + sx(hi) + 14, width - 200), top: 8 }}>
          <strong>{fmtDay(x[hi])}</strong>
          {series.map((s) => (
            <div key={s.key} className="tt-row"><span className="legend-key" style={{ background: s.color }} />{s.label}<span className="tt-val">{s.values[hi]?.toLocaleString()}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Meter (share of a whole) ---------------------------------------------------------------

export function Meter({ value, label }: { value: number; label: string }) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    <div className="meter-viz" role="meter" aria-valuenow={Math.round(pct * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className="meter-track"><div className="meter-bar" style={{ width: `${pct * 100}%` }} /></div>
      <span className="small">{Math.round(pct * 100)}%</span>
    </div>
  );
}
